"use client";

import React, { useEffect, useRef, useState } from "react";
import { ShoppingCart, CreditCard, ArrowRight, Info } from "lucide-react";
import { Button } from "../button";
import { cn } from "../../lib/utils";
import { BasketConflictNotice, type BasketChoiceNotice } from "./basket-conflict-notice";

export type { BasketChoiceNotice };

export type BasketChoiceStepProps = {
  /** Course/item name */
  itemName: string;
  /** Selected session/date info */
  dateLabel?: string;
  /** Total price */
  total: number;
  /** Currency code */
  currency?: string;
  /** Number of items already in basket */
  basketItemCount?: number;
  /** When user clicks "Add to Basket" */
  onAddToBasket: () => void;
  /** When user clicks "Proceed to Checkout" */
  onProceedToCheckout: () => void;
  /**
   * When true, skip the choice UI entirely and auto-fire onProceedToCheckout on mount.
   * Used by webcomponents where there is no basket drawer to review items in.
   */
  directCheckout?: boolean;
  /**
   * Set by the host when it refused the last choice (e.g. a request-to-book
   * ticket can't share the basket with what's already in it). Shown under
   * the choice cards, where the customer just clicked, until the host clears
   * it.
   */
  basketConflict?: BasketChoiceNotice | null;
};

/**
 * Basket Choice Step - shows after personal details when basket is enabled
 * Gives users the choice to either add to basket or proceed to checkout
 */
export function BasketChoiceStep({
  itemName,
  dateLabel,
  total,
  currency = "GBP",
  basketItemCount = 0,
  onAddToBasket,
  onProceedToCheckout,
  directCheckout = false,
  basketConflict = null,
}: BasketChoiceStepProps) {
  const hasAutoProceededRef = useRef(false);
  const conflictRef = useRef<HTMLDivElement>(null);
  // Bumped on every choice so each refusal brings the notice into view (the
  // cards stack on narrow screens and the notice can sit below the fold).
  // Not keyed on its wording: that follows the live basket, and changes while
  // the buyer edits it in the drawer above — no moment to scroll the page.
  const [choiceCount, setChoiceCount] = useState(0);
  const hasConflict = basketConflict !== null;

  useEffect(() => {
    if (directCheckout && !hasAutoProceededRef.current) {
      hasAutoProceededRef.current = true;
      onProceedToCheckout();
    }
  }, [directCheckout, onProceedToCheckout]);

  useEffect(() => {
    if (!hasConflict) return;
    conflictRef.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [hasConflict, choiceCount]);

  const choose = (handler: () => void) => () => {
    setChoiceCount((n) => n + 1);
    handler();
  };

  // Under directCheckout the step proceeds by itself, once. When the host
  // refuses that, there are no choice cards to show the reason under, and
  // "Continuing to checkout…" would never end — so the reason shows here,
  // with a way to continue by hand once it's fixed. Remembered (adjusted
  // while rendering) so the button stays after the notice clears.
  const [directRefused, setDirectRefused] = useState(false);
  if (directCheckout && basketConflict && !directRefused) setDirectRefused(true);

  if (directCheckout) {
    if (directRefused) {
      // Offered while the notice has a basket fix (or once it has cleared):
      // a refusal nothing in the basket can fix — the booking mixes on its
      // own — would only be refused again. The flow's footer keeps Back.
      const canContinue = !basketConflict || !!basketConflict.action;
      return (
        <div className="space-y-4 py-6">
          {basketConflict && <BasketConflictNotice ref={conflictRef} notice={basketConflict} />}
          {canContinue && (
            <div className="flex justify-center">
              <Button type="button" onClick={choose(onProceedToCheckout)}>
                Continue to checkout
              </Button>
            </div>
          )}
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-sm text-muted-foreground">Continuing to checkout…</p>
      </div>
    );
  }

  const formatPrice = (amount: number) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency,
    }).format(amount / 100);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-xl font-bold text-foreground uppercase tracking-wide">
          What would you like to do?
        </h2>
        <p className="mt-2 text-muted-foreground">
          You can add this to your basket and continue browsing, or proceed
          directly to checkout.
        </p>
      </div>

      {/* Item Summary */}
      <div className="bg-muted rounded-lg p-4 text-center">
        <p className="font-semibold text-foreground">{itemName}</p>
        {dateLabel && <p className="text-sm text-muted-foreground mt-1">{dateLabel}</p>}
        <p className="text-lg font-bold text-primary mt-2">
          {formatPrice(total)}
        </p>
      </div>

      {/* Choice Cards */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Proceed to Checkout Option (primary) */}
        <button
          type="button"
          onClick={choose(onProceedToCheckout)}
          className={cn(
            "relative flex flex-col items-center p-6 rounded-xl border-2",
            "bg-card hover:bg-muted transition-all duration-200",
            "border-primary hover:border-primary/80",
            "group cursor-pointer text-left",
          )}
        >
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
            <CreditCard className="w-8 h-8 text-primary" />
          </div>
          <h3 className="text-lg font-bold text-foreground mb-2">
            Proceed to Checkout
          </h3>
          <p className="text-sm text-muted-foreground text-center mb-4">
            Complete your booking now and pay for this item
          </p>
          <p className="text-xs text-primary font-medium">
            Pay {formatPrice(total)}
          </p>
          <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
            <ArrowRight className="w-5 h-5 text-primary" />
          </div>
        </button>

        {/* Add to Basket Option (secondary) */}
        <button
          type="button"
          onClick={choose(onAddToBasket)}
          className={cn(
            "relative flex flex-col items-center p-6 rounded-xl border-2",
            "bg-card hover:bg-muted transition-all duration-200",
            "border-border hover:border-muted-foreground/40",
            "group cursor-pointer text-left",
          )}
        >
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
            <ShoppingCart className="w-8 h-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-bold text-foreground mb-2">
            Add to Basket
          </h3>
          <p className="text-sm text-muted-foreground text-center mb-4">
            Save this item and continue browsing for more classes
          </p>
          {basketItemCount > 0 && (
            <p className="text-xs text-muted-foreground font-medium">
              {basketItemCount} item{basketItemCount !== 1 ? "s" : ""} already
              in basket
            </p>
          )}
          <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
            <ArrowRight className="w-5 h-5 text-muted-foreground" />
          </div>
        </button>
      </div>

      {/* Refused choice: why, and how to fix it */}
      {basketConflict && <BasketConflictNotice ref={conflictRef} notice={basketConflict} />}

      {/* Spot not reserved notice */}
      <div className="flex items-start gap-2.5 p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <Info className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
        <p className="text-xs text-amber-800">
          Adding to your basket does not reserve your spot. Places are only{" "}
          <b>confirmed</b> once <b>checkout</b> is complete.
        </p>
      </div>

      {/* Basket Info Banner — hidden while a conflict says the opposite */}
      {basketItemCount > 0 && !basketConflict && (
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <ShoppingCart className="w-5 h-5 text-primary flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-foreground">
                You have {basketItemCount} item
                {basketItemCount !== 1 ? "s" : ""} in your basket
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Add this item to your basket to checkout everything together
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
